'use client';

import { DocumentCheckIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { capitalCase } from 'change-case';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import Chip from '~/components/Chip';
import { SocialIcon } from '~/components/Icons/SocialIcons';
import Skeleton from '~/components/Loading/Skeleton';
import PendingLink from '~/components/Navigation/PendingLink';
import Pagination, { usePagination } from '~/components/Pagination';
import { columnSortOrder, SortableTableHeader } from '~/components/TableSortControl';
import Tooltip from '~/components/Tooltip';
import TruncatedText from '~/components/TruncatedText';
import { Routes, UNKNOWN_BRAND_ID } from '~/config';
import useQueryString from '~/hooks/useQueryString';
import { cn } from '~/lib/utils';
import { api, type RouterOutputs } from '~/utils/api';
import { getDocumentTitle } from '~/utils/document';

type Document = RouterOutputs['scanner']['getAllDocuments']['documents'][0];
type SortKey =
  | 'createdAt'
  | 'newFeedback'
  | 'unresolvedFeedback'
  | 'isReviewed'
  | 'title'
  | 'platform';

const itemsPerPage = 10;
const VALID_SORT_KEYS: SortKey[] = [
  'createdAt',
  'newFeedback',
  'unresolvedFeedback',
  'isReviewed',
  'title',
  'platform',
];

type ScannedPagesProps = {
  reportId: string;
  brandId: string | null;
};

export default function ScannedPages(props: ScannedPagesProps) {
  const router = useRouter();
  const params = useParams();
  const workspaceId = params?.workspaceId as string;
  const { currentPage, setCurrentPage, skip } = usePagination(itemsPerPage);
  const { searchParams, updateSearchParams } = useQueryString();
  const orderByParam = searchParams?.get('docsSort');
  const [orderByKey, orderByValue] = orderByParam?.split('_') || ['unresolvedFeedback', 'DESC'];

  const mappedOrderByKey = useMemo(() => {
    if (orderByKey === 'name' || orderByKey === 'brandName') return 'title' as const;
    if (!orderByKey || !VALID_SORT_KEYS.includes(orderByKey as SortKey)) {
      return 'unresolvedFeedback' as const;
    }
    return orderByKey as SortKey;
  }, [orderByKey]);

  useEffect(() => {
    if (!orderByParam && typeof window !== 'undefined') {
      updateSearchParams('docsSort', 'unresolvedFeedback_DESC');
    }
  }, [orderByParam, updateSearchParams]);

  const toggleSort = (key: SortKey) => {
    if (mappedOrderByKey === key && orderByValue === 'ASC') {
      updateSearchParams('docsSort', `${key}_DESC`);
      return;
    }
    updateSearchParams('docsSort', `${key}_ASC`);
  };

  const { data: getAllDocumentsResponse, isLoading } = api.scanner.getAllDocuments.useQuery(
    {
      skip,
      limit: itemsPerPage,
      filters: {
        reportId: props.reportId,
      },
      orderBy: {
        key: mappedOrderByKey,
        order: (orderByValue?.toLowerCase() as 'asc' | 'desc') || 'desc',
      },
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const documents = getAllDocumentsResponse?.documents || [];
  const total = getAllDocumentsResponse?.total || 0;

  return (
    <section aria-labelledby="pages-scanned-heading">
      <h3
        id="pages-scanned-heading"
        className="text-foreground mb-2 text-sm font-semibold tracking-tight"
      >
        Pages Scanned
      </h3>

      <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
          <table className="w-full min-w-[560px] table-fixed border-collapse text-left">
            <colgroup>
              <col />
              <col className="w-[88px]" />
              <col className="w-[120px]" />
              <col className="w-[88px]" />
              <col className="w-[96px]" />
            </colgroup>
            <thead>
              <tr className="text-muted-foreground text-[13px]">
                <SortableTableHeader
                  label="Page"
                  order={columnSortOrder(mappedOrderByKey, 'title', orderByValue as 'ASC' | 'DESC')}
                  onClick={() => toggleSort('title')}
                />
                <SortableTableHeader
                  label="Reviewed"
                  order={columnSortOrder(
                    mappedOrderByKey,
                    'isReviewed',
                    orderByValue as 'ASC' | 'DESC',
                  )}
                  onClick={() => toggleSort('isReviewed')}
                  align="center"
                />
                <SortableTableHeader
                  label="Platform"
                  order={columnSortOrder(
                    mappedOrderByKey,
                    'platform',
                    orderByValue as 'ASC' | 'DESC',
                  )}
                  onClick={() => toggleSort('platform')}
                />
                <SortableTableHeader
                  label="New"
                  order={columnSortOrder(
                    mappedOrderByKey,
                    'newFeedback',
                    orderByValue as 'ASC' | 'DESC',
                  )}
                  onClick={() => toggleSort('newFeedback')}
                  align="right"
                />
                <SortableTableHeader
                  label="Unresolved"
                  order={columnSortOrder(
                    mappedOrderByKey,
                    'unresolvedFeedback',
                    orderByValue as 'ASC' | 'DESC',
                  )}
                  onClick={() => toggleSort('unresolvedFeedback')}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-border/50 border-t" aria-hidden>
                    <td className="px-2 py-3.5">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="mx-auto size-4" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="h-4 w-16" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="ml-auto h-5 w-8" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="ml-auto h-5 w-8" />
                    </td>
                  </tr>
                ))
              ) : documents.length === 0 ? (
                <tr className="border-border/50 border-t">
                  <td colSpan={5} className="text-muted-foreground px-2 py-8 text-center text-sm">
                    No pages in this report.
                  </td>
                </tr>
              ) : (
                documents.map((row) => {
                  const isReviewed = !!row.ScannerRunDocuments?.[0]?.reviewedByUserId;
                  const platform =
                    row.ScannerRunDocuments[0]?.ScannerRun?.scannerJob?.scannerSocialMedia[0]
                      ?.type;
                  const scannerReportId = row.ScannerRunDocuments[0]?.scannerReportId;
                  const href = scannerReportId
                    ? `/${workspaceId}${Routes.CONTENT_SCANNER}/brand-reports/${props.brandId || UNKNOWN_BRAND_ID}/report/${scannerReportId}/scanned-page/${row.id}`
                    : null;
                  const newCount = row.newFeedback ?? 0;
                  const unresolvedCount = row.unresolvedFeedback ?? 0;
                  const title = getDocumentTitle(row);

                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-border/50 hover:bg-muted border-t transition-colors',
                        href && 'cursor-pointer',
                      )}
                      onClick={() => {
                        if (href) router.push(href);
                      }}
                    >
                      <td className="overflow-hidden px-2 py-3.5 align-middle">
                        {href ? (
                          <PendingLink
                            href={href}
                            skipPending
                            onClick={(e) => e.stopPropagation()}
                            className="focus-visible:ring-ring/50 -mx-2 -my-3.5 block min-w-0 px-2 py-3.5 outline-none focus-visible:ring-[3px]"
                          >
                            <div className="text-primary truncate text-sm font-medium hover:underline">
                              {title}
                            </div>
                            {row.link ? (
                              <TruncatedText as="div" className="text-muted-foreground text-xs">
                                {row.link}
                              </TruncatedText>
                            ) : null}
                          </PendingLink>
                        ) : (
                          <div className="min-w-0">
                            <div className="text-foreground truncate text-sm font-medium">
                              {title}
                            </div>
                            {row.link ? (
                              <TruncatedText as="div" className="text-muted-foreground text-xs">
                                {row.link}
                              </TruncatedText>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3.5 align-middle">
                        <div className="flex justify-center">
                          {isReviewed ? (
                            <Tooltip content="Reviewed" hideIcon>
                              <DocumentCheckIcon
                                height={16}
                                strokeWidth={2}
                                className="text-muted-foreground"
                                aria-label="Reviewed"
                              />
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </div>
                      </td>
                      <td className="overflow-hidden px-2 py-3.5 align-middle">
                        <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-sm">
                          {platform ? (
                            <SocialIcon className="h-3.5 shrink-0" type={platform} />
                          ) : (
                            <GlobeAltIcon className="size-3.5 shrink-0" strokeWidth={2} />
                          )}
                          <span className="truncate">
                            {platform ? capitalCase(platform) : 'Website'}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3.5 align-middle">
                        <div className="flex justify-end">
                          <Chip
                            level={newCount > 0 ? 'alert' : 'success'}
                            text={newCount}
                            className="px-2 py-0.5 text-xs tabular-nums"
                          />
                        </div>
                      </td>
                      <td className="px-2 py-3.5 align-middle">
                        <div className="flex justify-end">
                          <Chip
                            level={unresolvedCount > 0 ? 'alert' : 'success'}
                            text={unresolvedCount}
                            className="px-2 py-0.5 text-xs tabular-nums"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {total !== documents.length && total > 1 && (
          <div className="border-border/60 border-t px-3 py-2">
            <Pagination
              dense
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              total={Math.ceil(total / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={total}
              itemLabel="pages"
              rangeOnly
            />
          </div>
        )}
      </div>
    </section>
  );
}
